/**
 * Cloudflare Turnstile, rendered only when the API says bot protection is on.
 *
 * The script is loaded on demand, once, and only on the pages that need it (sign-in,
 * sign-up, password reset). The token is single-use: after a failed submit the widget
 * is reset so the next attempt gets a fresh one.
 */

import { useEffect, useEffectEvent, useImperativeHandle, useRef, type Ref } from 'react';

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'flexible' | 'compact';
    },
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile did not load'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('Turnstile could not be loaded'));
    };
    document.head.append(script);
  });
  return loading;
}

export interface TurnstileHandle {
  reset: () => void;
}

export function Turnstile({
  siteKey,
  onToken,
  ref,
}: {
  siteKey: string;
  onToken: (token: string | undefined) => void;
  ref?: Ref<TurnstileHandle>;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  // The widget's callbacks are registered once but always reach the latest `onToken`.
  const emit = useEffectEvent((token: string | undefined) => {
    onToken(token);
  });

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
      onToken(undefined);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    void loadTurnstile().then(
      (turnstile) => {
        if (cancelled || !container.current) return;
        widget.current = turnstile.render(container.current, {
          sitekey: siteKey,
          size: 'flexible',
          theme: 'auto',
          callback: (token) => {
            emit(token);
          },
          'expired-callback': () => {
            emit(undefined);
          },
          'error-callback': () => {
            emit(undefined);
          },
        });
      },
      () => {
        emit(undefined);
      },
    );
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey]);

  return <div ref={container} className="min-h-16" aria-label="Human check" />;
}
